import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatDialog } from '@angular/material/dialog';
import { debounceTime, distinctUntilChanged, tap } from 'rxjs/operators';
import { fromEvent, merge } from 'rxjs';
import { Store } from '@ngrx/store';
import { AppState } from '../../../../../../core/reducers';
import { LayoutUtilsService, QueryParamsModel } from '../../../../../../core/_base/crud';
import {
  PharmaciesDataSource,
  PharmaciesPageRequested,
  Pharmacy,
  PharmacyOnServerDeleted,
  PharmacyOnServerUpdated,
  ViewContext,
} from '../../../../../../core/lists';
import { TranslateService } from '@ngx-translate/core';
import { Update } from '@ngrx/entity';
import { StringUtilsService } from '../../../../../../core/services/string-utils.service';
import { FormBuilder, FormGroup } from '@angular/forms';
import { NavigationService } from '../../../../../../core/services/navigation.service';
import { NgxPermissionsService } from 'ngx-permissions';
import { TabletUtil } from '../../../../../../core/utils';

@Component({
  selector: 'kt-pharmacies-list',
  templateUrl: './pharmacies-list.component.html',
  styleUrls: ['./pharmacies-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PharmaciesListComponent implements OnInit, OnDestroy {
  dataSource: PharmaciesDataSource;
  displayedColumns = ['lastName', 'email', 'phone', 'fax', 'isActive', 'actions'];
  searchBarColumns = [
    'searchName',
    'searchEmail',
    'searchPhone',
    'searchFax',
    'searchIsActive',
    'searchActions',
  ];
  showSearchBar = false;
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
  @ViewChild('sort', { static: true }) sort: MatSort;
  @ViewChild('searchInput', { static: true }) searchInput: ElementRef;
  searchNameInput = '';
  searchEmailInput = '';
  searchPhoneInput = '';
  searchFaxInput = '';
  searchIsActiveSelect = 'true';
  statusTracker = 0;
  submissionErrorObj: any;
  hasSubmissionErrors: boolean;
  private subscriptions: any[] = [];
  searchForm: FormGroup;
  filtersApplied: any = false;
  firstLoadAfterBack: any = false;
  pageSize: any;

  constructor(
    private navigationService: NavigationService,
    private permissionsService: NgxPermissionsService,
    private tabletUtil: TabletUtil,
    public dialog: MatDialog,
    private activatedRoute: ActivatedRoute,
    private layoutUtilsService: LayoutUtilsService,
    private fb: FormBuilder,
    private store: Store<AppState>,
    private translate: TranslateService,
    public stringUtils: StringUtilsService,
  ) {}

  ngOnInit() {
    // Comment by Nourhan: please don't change the order of this line
    this.filtersApplied = this.navigationService.getFilters();

    const sortSubscription = this.sort.sortChange.subscribe(() => {
      this.paginator.pageIndex = this.paginator?.pageIndex || 0;
      this.sort.active = this.sort.direction ? this.sort.active : 'id';
      this.sort.direction = this.sort.direction || 'desc';
    });
    this.subscriptions.push({ id: 1, sub: sortSubscription });

    const paginatorSubscriptions = merge(this.sort.sortChange, this.paginator.page)
      .pipe(
        tap(() => {
          this.loadPharmaciesList();
        }),
      )
      .subscribe();
    this.subscriptions.push({ id: 2, sub: paginatorSubscriptions });

    const searchSubscription = fromEvent(this.searchInput.nativeElement, 'keyup')
      .pipe(
        debounceTime(700),
        distinctUntilChanged(),
        tap(() => {
          this.paginator.pageIndex = 0;
          this.loadPharmaciesList();
        }),
      )
      .subscribe();
    this.subscriptions.push({ id: 3, sub: searchSubscription });

    this.dataSource = new PharmaciesDataSource(this.store);

    this.searchForm = this.fb.group({
      search: [],
      status: ['true'],
    });

    // Comment by Nourhan: please don't change the order of this scope
    if (this.filtersApplied) {
      this.firstLoadAfterBack = true;
      this.pageSize = this.filtersApplied.pageSize;
    }

    const routeSubscription = this.activatedRoute.queryParams.subscribe(() => {
      this.loadPharmaciesList();
    });
    this.subscriptions.push({ id: 5, sub: routeSubscription });
  }

  ngOnDestroy() {
    this.subscriptions.forEach((el) => {
      if (el.sub) {
        el.sub.unsubscribe();
      }
    });
  }

  loadPharmaciesList() {
    const queryParams = new QueryParamsModel(
      this.filterConfiguration(),
      this.sort.direction || 'desc',
      this.sort.active || 'lastName',
      this.paginator.pageIndex,
      this.paginator.pageSize || this.pageSize || 25,
    );
    this.store.dispatch(new PharmaciesPageRequested({ page: queryParams }));
  }

  filterConfiguration(reset_applied_flag?: any): any {
    const filter: any = {};
    filter.isActive = this.searchIsActiveSelect;
    if (this.filtersApplied && this.firstLoadAfterBack) {
      this.firstLoadAfterBack = false;
      const appliedFilters = this.filtersApplied;
      filter.isActive = appliedFilters.isActive;
      this.searchIsActiveSelect = appliedFilters.isActive;
      this.showSearchBar = appliedFilters.showSearchBar;
      if (this.showSearchBar) {
        this.searchNameInput = appliedFilters.name;
        this.searchEmailInput = appliedFilters.email;
        this.searchPhoneInput = appliedFilters.phone;
        this.searchFaxInput = appliedFilters.fax;
      } else {
        if (this.searchInput?.nativeElement) {
          this.searchInput.nativeElement.value = appliedFilters.searchTerm || '';
        }
      }
      this.sort.direction = this.filtersApplied?.sortDirection || 'desc';
      this.sort.active = this.filtersApplied?.sortActive || 'lastName';
      this.paginator.pageIndex = this.filtersApplied?.pageIndex || 0;
      this.paginator.pageSize = this.filtersApplied?.pageSize || 25;
    }
    if (reset_applied_flag) this.filtersApplied = false;
    if (this.showSearchBar) {
      filter.name = this.searchNameInput;
      filter.email = this.searchEmailInput;
      filter.phone = this.searchPhoneInput?.replace(/[()\-\s]/g, '');
      filter.fax = this.searchFaxInput?.replace(/[()\-\s]/g, '');
      filter.showSearchBar = true;
    } else {
      filter.searchTerm = this.searchInput.nativeElement.value || '';
      filter.showSearchBar = false;
    }
    filter.sortDirection = this.sort.direction || 'desc';
    filter.sortActive = this.sort.active || 'lastName';
    filter.pageIndex = this.paginator.pageIndex;
    filter.pageSize = this.paginator.pageSize;
    return filter;
  }

  deletePharmacy(_item: Pharmacy) {
    const _title: string = this.translate.instant('PHARMACY.DELETE_PHARMACY_SIMPLE.TITLE');
    const _description: string = this.translate.instant(
      'PHARMACY.DELETE_PHARMACY_SIMPLE.DESCRIPTION',
    );
    const _waitDescription: string = this.translate.instant(
      'PHARMACY.DELETE_PHARMACY_SIMPLE.WAIT_DESCRIPTION',
    );

    const dialogRef = this.layoutUtilsService.deleteElement(_title, _description, _waitDescription);
    dialogRef.afterClosed().subscribe((res) => {
      if (!res) {
        return;
      }
      this.store.dispatch(new PharmacyOnServerDeleted({ id: _item.id }));
    });
  }

  updateStatusForPharmacy(pharmacy: Pharmacy) {
    const _title: string = this.translate.instant('PHARMACY.UPDATE_STATUS_SIMPLE.TITLE');
    const _description: string = this.translate.instant(
      'PHARMACY.UPDATE_STATUS_SIMPLE.DESCRIPTION',
    );
    const _waitDescription: string = this.translate.instant(
      'PHARMACY.UPDATE_STATUS_SIMPLE.WAIT_DESCRIPTION',
    );

    const dialogRef = this.layoutUtilsService.confirmAction(_title, _description, _waitDescription);
    dialogRef.afterClosed().subscribe((res) => {
      if (!res) {
        return;
      }
      const newPharmacy: Pharmacy = Object.assign(new Pharmacy(), pharmacy);
      newPharmacy.changeActiveStatus();
      const updatePharmacy: Update<Pharmacy> = {
        id: newPharmacy.id,
        changes: newPharmacy,
      };
      this.store.dispatch(
        new PharmacyOnServerUpdated({
          partialPharmacy: updatePharmacy,
          pharmacy: newPharmacy,
        }),
      );
    });
  }

  canViewPharmacy(): boolean {
    return !!this.permissionsService.getPermission('canViewPharmacy');
  }

  canEditPharmacy(): boolean {
    return !!this.permissionsService.getPermission('canEditPharmacy');
  }

  createPharmacy() {
    this.navigationService.navigateTo('lists/pharmacies/add');
    this.navigationService.updateURLWithFiltersOnList(this.filterConfiguration(true));
  }

  viewPharmacy(pharmacy: Pharmacy) {
    if (!this.canViewPharmacy()) return;

    if (this.canEditPharmacy()) {
      this.editPharmacy(pharmacy.id);
      return;
    }
    const queryParams = {
      viewContext: ViewContext.pharmacies,
    };
    this.navigationService.navigateTo('lists/pharmacies/view/' + pharmacy.id, queryParams);
    this.navigationService.updateURLWithFiltersOnList(this.filterConfiguration(true));
  }

  viewPharmacyDblclick(ref: string, pharmacy: Pharmacy): void {
    this.tabletUtil.handleDblclick(ref, this.viewPharmacy.bind(this, pharmacy));
  }

  editPharmacy(id: number) {
    const queryParams = {
      viewContext: ViewContext.pharmacies,
    };

    this.navigationService.navigateTo('lists/pharmacies/edit/' + id, queryParams);
    this.navigationService.updateURLWithFiltersOnList(this.filterConfiguration(true));
  }

  getItemCssClassByStatus(status: string): string {
    switch (status) {
      case 'false':
        return 'danger';
      case 'true':
        return 'success';
      default:
        return 'warning';
    }
  }

  getItemStatusString(status: string): string {
    switch (status) {
      case 'false':
        return this.translate.instant('GLOBAL.INACTIVE');
      case 'true':
        return this.translate.instant('GLOBAL.ACTIVE');
      default:
        return this.translate.instant('GLOBAL.ALL');
    }
  }

  reloadPharmaciesList(): void {
    this.paginator.pageIndex = 0;
    this.loadPharmaciesList();
  }

  toggleStatus(event: Event): void {
    event.stopPropagation();

    this.statusTracker = (this.statusTracker + 1) % 3;

    switch (this.statusTracker) {
      case 0:
        this.searchIsActiveSelect = 'true';
        break;
      case 1:
        this.searchIsActiveSelect = 'false';
        break;
      case 2:
        this.searchIsActiveSelect = '';
        break;
    }

    this.reloadPharmaciesList();
  }

  handleStatusChange(): void {
    switch (this.searchIsActiveSelect) {
      case 'true':
        this.statusTracker = 0;
        break;
      case 'false':
        this.statusTracker = 1;
        break;
      case '':
        this.statusTracker = 2;
        break;
    }
  }

  resetSearchBar(event?: Event): void {
    event?.stopPropagation();
    this.searchNameInput = '';
    this.searchEmailInput = '';
    this.searchPhoneInput = '';
    this.searchFaxInput = '';
  }

  resetSearchInput(event: Event, inputEl: HTMLInputElement, reload = false): void {
    event.stopPropagation();

    inputEl.value = '';

    if (reload) this.reloadPharmaciesList();
  }

  toggleSearchBar(event: Event): void {
    event.stopPropagation();

    this.showSearchBar = !this.showSearchBar;

    if (this.showSearchBar) {
      this.searchInput.nativeElement.value = '';
      this.searchInput.nativeElement.disabled = true;
    } else {
      this.resetSearchBar();
      this.searchInput.nativeElement.disabled = false;
      this.searchInput.nativeElement.focus();
    }

    this.reloadPharmaciesList();
  }

  handleSearchByField(event: Event): void {
    event.stopPropagation();
    this.reloadPharmaciesList();
  }
}

